import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Compass, 
  Battery, 
  BookOpen, 
  Clock, 
  Sparkles, 
  GitMerge, 
  LineChart,
  ArrowRight,
  ChevronRight
} from 'lucide-react';

const tools = [
  {
    id: 'radar',
    name: 'Radar de Decisões Difíceis',
    description: 'Naveguem temas sensíveis como família, orçamento e tradições com cenários reais e frases prontas.',
    icon: Compass,
    color: '#B5C4B1',
    path: '/ferramentas/radar'
  },
  {
    id: 'energy',
    name: 'Planeamento por Energia',
    description: 'Tarefas adaptadas ao vosso estado emocional. Respeitem o vosso ritmo.',
    icon: Battery,
    color: '#D4C4B0',
    path: '/ferramentas/energia'
  },
  {
    id: 'library',
    name: 'Biblioteca de Decisões Reais',
    description: 'Histórias honestas de casais: o que valeu a pena, arrependimentos e compromissos.',
    icon: BookOpen,
    color: '#E8D5D5',
    path: '/ferramentas/biblioteca'
  },
  {
    id: 'nextday',
    name: 'Cláusula do Dia Seguinte',
    description: 'Antes de decidir, façam uma pausa. Esta ferramenta ajuda a reduzir arrependimentos.',
    icon: Clock,
    color: '#C5D1DC',
    path: '/ferramentas/clausula'
  },
  {
    id: 'traditions',
    name: 'Modo de Tradições',
    description: 'Escolham seguir, adaptar ou ignorar cada tradição. O casamento adapta-se a vocês.',
    icon: Sparkles,
    color: '#B5C4B1',
    path: '/ferramentas/tradicoes'
  },
  {
    id: 'concessions',
    name: 'Mapa de Concessões',
    description: 'Identifiquem prioridades individuais e encontrem zonas de consenso antes dos conflitos.',
    icon: GitMerge,
    color: '#D4C4B0',
    path: '/ferramentas/concessoes'
  },
  {
    id: 'timeline',
    name: 'Linha do Tempo Emocional',
    description: 'Visualizem as fases emocionais do planeamento. Normalizem o que sentem.',
    icon: LineChart,
    color: '#E8D5D5',
    path: '/ferramentas/timeline'
  }
];

const ToolsDashboard = ({ profile }) => {
  const navigate = useNavigate();
  const [hoveredTool, setHoveredTool] = useState(null);

  return (
    <div className="min-h-screen py-12 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/70 backdrop-blur-sm rounded-full text-sm text-[#8B7D6B] mb-6 shadow-sm">
            <Sparkles size={16} className="text-[#C4B5A0]" />
            Ferramentas exclusivas para o vosso perfil
          </div>
          
          <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl text-[#2C2C2C] mb-4">
            A Vossa Área de Planeamento
          </h1>
          
          <p className="text-lg text-[#6B4E37] max-w-2xl mx-auto">
            Ferramentas pensadas para casais que querem decidir com clareza, 
            não por pressão. Explorem ao vosso ritmo.
          </p>

          {profile && (
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-white/80 rounded-lg shadow-sm">
              <span className="text-2xl">{profile.emoji}</span>
              <span className="text-[#6B4E37]">Perfil: <strong>{profile.name}</strong></span>
            </div>
          )}
        </div>

        {/* Tools Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {tools.map((tool) => {
            const Icon = tool.icon;
            const isHovered = hoveredTool === tool.id;
            
            return (
              <button
                key={tool.id}
                onClick={() => navigate(tool.path)}
                onMouseEnter={() => setHoveredTool(tool.id)}
                onMouseLeave={() => setHoveredTool(null)}
                className="group bg-white/80 backdrop-blur-sm rounded-2xl p-6 text-left shadow-sm hover:shadow-xl transition-all duration-300 border border-transparent hover:border-[#D4C4B0]/30"
              >
                {/* Icon */}
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110"
                  style={{ backgroundColor: `${tool.color}30` }}
                >
                  <Icon size={28} style={{ color: tool.color }} />
                </div>

                {/* Title */}
                <h3 className="font-['Cormorant_Garamond'] text-xl text-[#2C2C2C] mb-2 group-hover:text-[#6B4E37] transition-colors">
                  {tool.name}
                </h3>

                {/* Description */}
                <p className="text-sm text-[#8B7D6B] mb-4 leading-relaxed">
                  {tool.description}
                </p>

                {/* CTA */}
                <div className="flex items-center gap-2 text-sm font-medium text-[#C4B5A0] group-hover:text-[#6B4E37] transition-colors">
                  Explorar
                  <ChevronRight 
                    size={16} 
                    className={`transition-transform duration-300 ${isHovered ? 'translate-x-1' : ''}`} 
                  />
                </div>
              </button>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-8 max-w-2xl mx-auto">
            <p className="text-[#6B4E37] mb-4">
              Estas ferramentas foram criadas para vos apoiar, não para vos pressionar. 
              Usem o que fizer sentido, ignorem o resto.
            </p>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 text-[#C4B5A0] hover:text-[#6B4E37] transition-colors"
            >
              Voltar ao site principal
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToolsDashboard;
